import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '../lib/api';
import { useAuth } from '../context/AuthContext';

const EyeSvg = () => (
  <svg xmlns="http://www.w3.org/2000/svg" width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"/>
    <circle cx="12" cy="12" r="3"/>
  </svg>
);
const EyeOffSvg = () => (
  <svg xmlns="http://www.w3.org/2000/svg" width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M17.94 17.94A10.07 10.07 0 0 1 12 20c-7 0-11-8-11-8a18.45 18.45 0 0 1 5.06-5.94M9.9 4.24A9.12 9.12 0 0 1 12 4c7 0 11 8 11 8a18.5 18.5 0 0 1-2.16 3.19m-6.72-1.07a3 3 0 1 1-4.24-4.24M1 1l22 22"/>
  </svg>
);

export default function AccountSettings() {
  const { user, refreshSession, clearMustChangePassword } = useAuth();
  const navigate = useNavigate();

  // ── Display name ──────────────────────────────────────────────────────────
  const [name,        setName]        = useState(user?.name || '');
  const [nameSaving,  setNameSaving]  = useState(false);
  const [nameMsg,     setNameMsg]     = useState(null); // { ok: bool, text }

  async function handleNameSave(e) {
    e.preventDefault();
    if (!name.trim() || name.trim() === user?.name) return;
    setNameSaving(true);
    setNameMsg(null);
    try {
      const res = await api.patch('/api/auth/profile', { name: name.trim() });
      refreshSession(res.data.accessToken, res.data.refreshToken);
      setNameMsg({ ok: true, text: 'Name updated.' });
    } catch (err) {
      setNameMsg({ ok: false, text: err.response?.data?.error || 'Update failed.' });
    } finally {
      setNameSaving(false);
    }
  }

  // ── Password change ───────────────────────────────────────────────────────
  const [currentPw,    setCurrentPw]    = useState('');
  const [newPw,        setNewPw]        = useState('');
  const [confirmPw,    setConfirmPw]    = useState('');
  const [showCurrent,  setShowCurrent]  = useState(false);
  const [showNew,      setShowNew]      = useState(false);
  const [showConfirm,  setShowConfirm]  = useState(false);
  const [pwSaving,     setPwSaving]     = useState(false);
  const [pwMsg,        setPwMsg]        = useState(null);

  async function handlePwChange(e) {
    e.preventDefault();
    setPwMsg(null);
    if (newPw !== confirmPw) { setPwMsg({ ok: false, text: 'New passwords do not match.' }); return; }
    if (newPw.length < 8)    { setPwMsg({ ok: false, text: 'New password must be at least 8 characters.' }); return; }
    if (newPw === currentPw) { setPwMsg({ ok: false, text: 'New password must differ from current password.' }); return; }

    setPwSaving(true);
    try {
      const res = await api.post('/api/auth/change-password', { currentPassword: currentPw, newPassword: newPw });
      clearMustChangePassword(res.data.accessToken, res.data.refreshToken, res.data.user);
      setCurrentPw(''); setNewPw(''); setConfirmPw('');
      setPwMsg({ ok: true, text: 'Password updated.' });
    } catch (err) {
      setPwMsg({ ok: false, text: err.response?.data?.error || 'Password change failed.' });
    } finally {
      setPwSaving(false);
    }
  }

  // ── Notification preferences ─────────────────────────────────────────────
  const [notifPrefs,     setNotifPrefs]     = useState(null);  // [{ eventType, emailEnabled, inAppEnabled }]
  const [notifTypes,     setNotifTypes]     = useState([]);    // [{ type, label, description, roles }]
  const [notifLoading,   setNotifLoading]   = useState(true);
  const [notifSaving,    setNotifSaving]    = useState(false);
  const [notifMsg,       setNotifMsg]       = useState(null);

  useEffect(() => {
    Promise.all([
      api.get('/api/notifications/event-types'),
      api.get('/api/notifications/event-preferences'),
    ]).then(([typesRes, prefsRes]) => {
      setNotifTypes(typesRes.data);
      // Index prefs by eventType for easy lookup
      const map = {};
      prefsRes.data.forEach(p => { map[p.eventType] = p; });
      setNotifPrefs(map);
    }).catch(() => {}).finally(() => setNotifLoading(false));
  }, []);

  function togglePref(eventType, channel) {
    setNotifPrefs(prev => ({
      ...prev,
      [eventType]: {
        ...prev[eventType],
        eventType,
        [channel]: !prev[eventType]?.[channel],
      },
    }));
    setNotifMsg(null);
  }

  async function handleNotifSave() {
    setNotifSaving(true);
    setNotifMsg(null);
    try {
      const payload = Object.values(notifPrefs).map(p => ({
        eventType:    p.eventType,
        emailEnabled: p.emailEnabled  ?? true,
        inAppEnabled: p.inAppEnabled  ?? true,
      }));
      await api.put('/api/notifications/event-preferences', payload);
      setNotifMsg({ ok: true, text: 'Preferences saved.' });
    } catch {
      setNotifMsg({ ok: false, text: 'Failed to save preferences.' });
    } finally {
      setNotifSaving(false);
    }
  }

  // Only show event types relevant to the current user's roles
  const userRoles = user?.roles || [];
  const visibleTypes = notifTypes.filter(t =>
    t.roles.some(r => userRoles.includes(r))
  );

  // ── Render ────────────────────────────────────────────────────────────────
  return (
    <div className="main">
      <div className="topbar">
        <div>
          <div className="page-title">Account Settings</div>
          <div className="page-sub">{user?.email}</div>
        </div>
      </div>

      <div className="body" style={{ display: 'flex', flexDirection: 'column', gap: 24, maxWidth: 520 }}>

        {/* ── Display name ── */}
        <div className="editor-card" style={{ padding: '20px 24px' }}>
          <div style={{ fontSize: 13, fontWeight: 700, color: 'var(--tx-primary)', marginBottom: 16 }}>
            Display Name
          </div>
          <form onSubmit={handleNameSave} style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
            <div className="vc-field full">
              <label>Name</label>
              <input
                className="vc-input"
                value={name}
                onChange={e => { setName(e.target.value); setNameMsg(null); }}
                placeholder="Your display name"
                autoComplete="name"
              />
            </div>
            {nameMsg && (
              <div style={{ fontSize: 12, color: nameMsg.ok ? 'var(--s-approved-t)' : 'var(--s-incomplete-t)' }}>
                {nameMsg.text}
              </div>
            )}
            <div>
              <button
                type="submit"
                className="btn-primary"
                disabled={nameSaving || !name.trim() || name.trim() === user?.name}
              >
                {nameSaving ? 'Saving…' : 'Save Name'}
              </button>
            </div>
          </form>
        </div>

        {/* ── Password ── */}
        <div className="editor-card" style={{ padding: '20px 24px' }}>
          <div style={{ fontSize: 13, fontWeight: 700, color: 'var(--tx-primary)', marginBottom: 16 }}>
            Change Password
          </div>
          <form onSubmit={handlePwChange} style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
            <div className="vc-field full">
              <label>Current Password</label>
              <div style={{ position: 'relative' }}>
                <input
                  className="vc-input"
                  type={showCurrent ? 'text' : 'password'}
                  value={currentPw}
                  onChange={e => { setCurrentPw(e.target.value); setPwMsg(null); }}
                  placeholder="Your current password"
                  autoComplete="current-password"
                  style={{ paddingRight: 40 }}
                />
                <button type="button" tabIndex={-1} onClick={() => setShowCurrent(p => !p)} style={{ position: 'absolute', right: 10, top: '50%', transform: 'translateY(-50%)', background: 'none', border: 'none', cursor: 'pointer', padding: 4, color: 'var(--tx-muted)', display: 'flex', alignItems: 'center', lineHeight: 1 }}>
                  {showCurrent ? <EyeOffSvg /> : <EyeSvg />}
                </button>
              </div>
            </div>
            <div className="vc-field full">
              <label>New Password</label>
              <div style={{ position: 'relative' }}>
                <input
                  className="vc-input"
                  type={showNew ? 'text' : 'password'}
                  value={newPw}
                  onChange={e => { setNewPw(e.target.value); setPwMsg(null); }}
                  placeholder="At least 8 characters"
                  autoComplete="new-password"
                  style={{ paddingRight: 40 }}
                />
                <button type="button" tabIndex={-1} onClick={() => setShowNew(p => !p)} style={{ position: 'absolute', right: 10, top: '50%', transform: 'translateY(-50%)', background: 'none', border: 'none', cursor: 'pointer', padding: 4, color: 'var(--tx-muted)', display: 'flex', alignItems: 'center', lineHeight: 1 }}>
                  {showNew ? <EyeOffSvg /> : <EyeSvg />}
                </button>
              </div>
            </div>
            <div className="vc-field full">
              <label>Confirm New Password</label>
              <div style={{ position: 'relative' }}>
                <input
                  className="vc-input"
                  type={showConfirm ? 'text' : 'password'}
                  value={confirmPw}
                  onChange={e => { setConfirmPw(e.target.value); setPwMsg(null); }}
                  placeholder="Repeat new password"
                  autoComplete="new-password"
                  style={{ paddingRight: 40 }}
                />
                <button type="button" tabIndex={-1} onClick={() => setShowConfirm(p => !p)} style={{ position: 'absolute', right: 10, top: '50%', transform: 'translateY(-50%)', background: 'none', border: 'none', cursor: 'pointer', padding: 4, color: 'var(--tx-muted)', display: 'flex', alignItems: 'center', lineHeight: 1 }}>
                  {showConfirm ? <EyeOffSvg /> : <EyeSvg />}
                </button>
              </div>
            </div>
            {pwMsg && (
              <div style={{ fontSize: 12, color: pwMsg.ok ? 'var(--s-approved-t)' : 'var(--s-incomplete-t)' }}>
                {pwMsg.text}
              </div>
            )}
            <div>
              <button
                type="submit"
                className="btn-primary"
                disabled={pwSaving || !currentPw || !newPw || !confirmPw}
              >
                {pwSaving ? 'Updating…' : 'Update Password'}
              </button>
            </div>
          </form>
        </div>

        {/* ── Read-only info ── */}
        <div className="editor-card" style={{ padding: '20px 24px' }}>
          <div style={{ fontSize: 13, fontWeight: 700, color: 'var(--tx-primary)', marginBottom: 16 }}>
            Account Info
          </div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
            <div style={{ display: 'flex', gap: 16 }}>
              <span style={{ fontSize: 12, color: 'var(--tx-muted)', width: 80, flexShrink: 0 }}>Email</span>
              <span style={{ fontSize: 12, color: 'var(--tx-primary)' }}>{user?.email || '—'}</span>
            </div>
            <div style={{ display: 'flex', gap: 16 }}>
              <span style={{ fontSize: 12, color: 'var(--tx-muted)', width: 80, flexShrink: 0 }}>Initials</span>
              <span style={{ fontSize: 12, fontFamily: 'var(--mono)', color: 'var(--violet)' }}>{user?.initials || '—'}</span>
            </div>
            <div style={{ display: 'flex', gap: 16 }}>
              <span style={{ fontSize: 12, color: 'var(--tx-muted)', width: 80, flexShrink: 0 }}>Roles</span>
              <span style={{ fontSize: 12, color: 'var(--tx-primary)' }}>{user?.roles?.join(', ') || '—'}</span>
            </div>
          </div>
          <div style={{ fontSize: 11, color: 'var(--tx-faint)', marginTop: 14 }}>
            Email, initials, and roles can only be changed by an Admin.
          </div>
        </div>

        {/* ── Notification preferences ── */}
        <div className="editor-card" style={{ padding: '20px 24px' }}>
          <div style={{ fontSize: 13, fontWeight: 700, color: 'var(--tx-primary)', marginBottom: 4 }}>
            Notification Preferences
          </div>
          <div style={{ fontSize: 12, color: 'var(--tx-faint)', marginBottom: 18, lineHeight: 1.5 }}>
            Control which notifications you receive in-app and by email.
            Transactional emails (password reset, shoot assignments) are always sent.
          </div>

          {notifLoading ? (
            <div style={{ fontSize: 12, color: 'var(--tx-faint)' }}>Loading…</div>
          ) : visibleTypes.length === 0 ? (
            <div style={{ fontSize: 12, color: 'var(--tx-faint)' }}>No configurable notifications for your role.</div>
          ) : (
            <>
              <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12 }}>
                <thead>
                  <tr>
                    <th style={{ textAlign: 'left', padding: '0 0 10px', color: 'var(--tx-muted)', fontWeight: 700, fontSize: 10, textTransform: 'uppercase', letterSpacing: '0.05em' }}>
                      Event
                    </th>
                    <th style={{ textAlign: 'center', padding: '0 12px 10px', color: 'var(--tx-muted)', fontWeight: 700, fontSize: 10, textTransform: 'uppercase', letterSpacing: '0.05em', whiteSpace: 'nowrap' }}>
                      In-App
                    </th>
                    <th style={{ textAlign: 'center', padding: '0 0 10px', color: 'var(--tx-muted)', fontWeight: 700, fontSize: 10, textTransform: 'uppercase', letterSpacing: '0.05em' }}>
                      Email
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {visibleTypes.map((t, i) => {
                    const pref = notifPrefs?.[t.type] || {};
                    const inApp = pref.inAppEnabled ?? true;
                    const email = pref.emailEnabled ?? true;
                    return (
                      <tr key={t.type} style={{ borderTop: i === 0 ? '1px solid var(--border)' : '1px solid var(--border)' }}>
                        <td style={{ padding: '12px 0' }}>
                          <div style={{ color: 'var(--tx-primary)', fontWeight: 500, marginBottom: 2 }}>{t.label}</div>
                          <div style={{ color: 'var(--tx-faint)', fontSize: 11, lineHeight: 1.4 }}>{t.description}</div>
                        </td>
                        <td style={{ textAlign: 'center', padding: '12px 12px' }}>
                          <button
                            type="button"
                            onClick={() => togglePref(t.type, 'inAppEnabled')}
                            style={{
                              width: 36, height: 20, borderRadius: 10,
                              background: inApp ? 'var(--violet)' : 'var(--bg-input)',
                              border: `1px solid ${inApp ? 'var(--violet)' : 'var(--border-mid)'}`,
                              cursor: 'pointer', position: 'relative', flexShrink: 0,
                              transition: 'background 0.15s, border-color 0.15s',
                            }}
                            aria-label={`Toggle in-app for ${t.label}`}
                          >
                            <span style={{
                              position: 'absolute', top: 2,
                              left: inApp ? 16 : 2,
                              width: 14, height: 14, borderRadius: '50%',
                              background: '#fff',
                              transition: 'left 0.15s',
                              boxShadow: '0 1px 3px rgba(0,0,0,0.3)',
                            }} />
                          </button>
                        </td>
                        <td style={{ textAlign: 'center', padding: '12px 0' }}>
                          <button
                            type="button"
                            onClick={() => togglePref(t.type, 'emailEnabled')}
                            style={{
                              width: 36, height: 20, borderRadius: 10,
                              background: email ? 'var(--violet)' : 'var(--bg-input)',
                              border: `1px solid ${email ? 'var(--violet)' : 'var(--border-mid)'}`,
                              cursor: 'pointer', position: 'relative', flexShrink: 0,
                              transition: 'background 0.15s, border-color 0.15s',
                            }}
                            aria-label={`Toggle email for ${t.label}`}
                          >
                            <span style={{
                              position: 'absolute', top: 2,
                              left: email ? 16 : 2,
                              width: 14, height: 14, borderRadius: '50%',
                              background: '#fff',
                              transition: 'left 0.15s',
                              boxShadow: '0 1px 3px rgba(0,0,0,0.3)',
                            }} />
                          </button>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>

              {notifMsg && (
                <div style={{ fontSize: 12, marginTop: 14, color: notifMsg.ok ? 'var(--s-approved-t)' : 'var(--s-incomplete-t)' }}>
                  {notifMsg.text}
                </div>
              )}

              <div style={{ marginTop: 16 }}>
                <button className="btn-primary" onClick={handleNotifSave} disabled={notifSaving || !notifPrefs}>
                  {notifSaving ? 'Saving…' : 'Save Preferences'}
                </button>
              </div>
            </>
          )}
        </div>

        {/* ── Time Off ── */}
        <div className="editor-card" style={{ padding: '20px 24px' }}>
          <div style={{ fontSize: 13, fontWeight: 700, color: 'var(--tx-primary)', marginBottom: 4 }}>
            Time Off
          </div>
          <div style={{ fontSize: 12, color: 'var(--tx-faint)', marginBottom: 16, lineHeight: 1.5 }}>
            Request time off and track your PTO balance.
          </div>
          <button className="btn-primary" onClick={() => navigate('/time-off')}>
            Time Off
          </button>
        </div>

      </div>
    </div>
  );
}
